import type { DeliveryZone, Fraction, FractionKey } from './types';
import { pickupDateFor } from './workdays';

export const COMPANY = {
  name: 'Bergen Miljø & Gjenvinning',
  legalName: 'Bergen Miljø & Gjenvinning AS',
  shortName: 'BMG',
  address: 'Glasskaret 1D',
  postalCode: '5106 Øvre Ervik',
  // [TRENGER FRA KUNDE: bekreft ett hovedtelefonnummer]
  phone: '55 25 65 00',
  phoneRaw: '+4755256500',
  // [TRENGER FRA KUNDE: bekreft én bestillings-e-post]
  email: 'bestilling@bmgas.no',
  generalEmail: 'post@bmgas.no',
  hours: 'Man–fre 07:00–21:00 (telefon)',
  deliveryHours: 'Man–fre 07:00–18:00 (utkjøring)',
  facebook: 'https://www.facebook.com/BMGjenvinning',
  mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Glasskaret+1D+5106+%C3%98vre+Ervik',
  // [TRENGER FRA KUNDE: Vipps merchant number]
  vippsMerchant: '',
};

export const FRACTIONS: Fraction[] = [
  {
    key: 'restavfall',
    name: 'Restavfall / blandet avfall',
    description:
      'Blandet bygge- og rivingsavfall som ikke kan gjenvinnes separat. Alt som går i en standard container når du rydder, river eller bygger om.',
    cannotInclude: [
      'Tekstiler og klær',
      'EE-avfall (elektronikk)',
      'Farlig avfall (maling, løsemidler, olje)',
      'Dekk og gummi',
      'Gips',
      'Isolasjon',
      'Asbest',
      'Trykkimpregnert trevirke',
    ],
    allowed: [],
    warning: null,
    active: true,
    imagePrompt: 'container with mixed construction waste on a Norwegian building site',
  },
  {
    key: 'trevirke',
    name: 'Trevirke',
    description:
      'Ubehandlet, malt, lakkert og beiset treverk fra byggeplass, riving eller oppussing. Spiker og små metallbeslag er greit. Trykkimpregnert treverk skal ikke i denne containeren, men i containeren for trykkimpregnert.',
    cannotInclude: [
      'Trykkimpregnert trevirke (bruk containeren for trykkimpregnert)',
      'Plast, metall, gips eller isolasjon',
    ],
    allowed: [],
    warning: null,
    active: true,
    imagePrompt: 'wooden planks and timber in a clean waste container',
  },
  {
    key: 'hageavfall',
    name: 'Hageavfall',
    description:
      'Kun greiner, kvister, blader og busker. Hageavfall betyr ikke alt som står i hagen. Feilsortering gir avviksgebyr.',
    cannotInclude: [
      'Hagemøbler',
      'Grill',
      'Jord',
      'Røtter med jord',
      'Stein og grus',
      'Tømmer og stubber',
      'Betong',
      'Treverk',
      'Metall',
    ],
    allowed: [],
    warning: 'Kun greiner, kvister, blader og busker. Ikke hagemøbler, grill, jord, stein, stubber, treverk eller metall.',
    active: true,
    imagePrompt: 'garden waste with branches and leaves in a green container',
  },
  {
    key: 'trykkimpregnert',
    name: 'Trykkimpregnert trevirke',
    description:
      'Trykkimpregnert treverk må i egen container. Det inneholder kjemikalier og kan ikke blandes med vanlig trevirke eller restavfall.',
    cannotInclude: ['Vanlig trevirke (ubehandlet, malt, lakkert eller beiset)', 'Plast', 'Metall', 'Gips', 'Isolasjon'],
    allowed: [],
    warning: null,
    active: true,
    imagePrompt: 'pressure-treated wooden posts and planks in a waste container',
  },
  {
    key: 'gips',
    name: 'Gips',
    description:
      'Ren gips fra riving eller rehab. Gipsplater uten store mengder lim, maling eller annet materiale. Gips gjenvinnes separat og må holdes ren.',
    cannotInclude: ['Trevirke', 'Plast', 'Metall', 'Isolasjon', 'Keramikk og fliser'],
    allowed: [],
    warning: null,
    active: true,
    imagePrompt: 'stack of gypsum boards and plaster waste in a container',
  },
  {
    key: 'isolasjon',
    name: 'Isolasjon',
    description:
      'Mineralull, steinull, glassull og lignende isolasjonsmaterialer. Må holdes atskilt fra annet avfall for riktig behandling.',
    cannotInclude: ['Trevirke', 'Plast', 'Gips', 'Metall', 'Farlig avfall'],
    allowed: [],
    warning: null,
    active: true,
    imagePrompt: 'insulation wool material being placed in a waste container',
  },
  {
    key: 'tunge_masser',
    name: 'Tunge masser',
    description:
      'Rene masser, masser med betong og fliser, betongtakstein og skifer og glassert eller malt takstein. Tunge materialer som krever egnet container og egnet mottak. Veies ved mottak.',
    cannotInclude: ['Trevirke', 'Plast', 'Metall', 'Farlig avfall', 'Gips', 'Isolasjon'],
    allowed: [],
    warning: null,
    active: true,
    imagePrompt: 'concrete rubble and stones in a heavy-duty waste container',
  },
  {
    key: 'storsekker',
    name: 'Storsekk (Big Bag)',
    description:
      'Fyllbar storsekk for mindre mengder avfall der en container er for stor. Leveres til din adresse.',
    cannotInclude: ['Farlig avfall', 'EE-avfall', 'Dekk og gummi'],
    allowed: [],
    warning: null,
    active: true,
    imagePrompt: 'a large industrial big bag container for waste on a driveway',
  },
];

export const DELIVERY_ZONES: DeliveryZone[] = [
  {
    name: 'Standard dekningsområde',
    areas: ['Bergen', 'Os', 'Askøy', 'Arna', 'Osterøy', 'Austrheim'],
    surcharge: 0,
  },
  {
    name: 'Tilleggssone',
    areas: ['Øygarden (inkl. Sund)', 'Ytre Askøy'],
    // [TRENGER FRA KUNDE: eksakte kjøretillegg i kroner per sone]
    surcharge: 0,
  },
];

export const OS_TOLL_PER_TRIP_NOK = 129;
export const OS_ZONE_SURCHARGE_NOK = OS_TOLL_PER_TRIP_NOK * 2;

export function isOsZonePostalCode(postalCode: string): boolean {
  return /^52[01]\d$/.test(postalCode.trim());
}

export function surchargeLineLabel(zone: string | null | undefined): string {
  return zone === 'os' ? `Kjøretillegg Os (bompenger), 2 × ${OS_TOLL_PER_TRIP_NOK} kr` : 'Kjøretillegg';
}

export function formatNok(amount: number): string {
  return new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency: 'NOK',
    minimumFractionDigits: Number.isInteger(Number(amount)) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(Number(amount)) ? 0 : 2,
  }).format(amount);
}

export function formatNorwegianDate(dateStr: string): string {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr || '—';
  const [year, month, day] = dateStr.split('-');
  const y = parseInt(year, 10);
  if (y < 2000 || y > 2100) return '—';
  const date = new Date(y, parseInt(month, 10) - 1, parseInt(day, 10));
  if (isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function formatWeekdayDate(dateStr: string): string {
  const date = new Date(`${dateStr}T12:00:00Z`);
  if (isNaN(date.getTime())) return dateStr;
  return new Intl.DateTimeFormat('nb-NO', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date);
}

export function getFractionByKey(key: string): Fraction | undefined {
  return FRACTIONS.find((f) => f.key === key);
}

export const TUNGE_MASSER_SUBTYPES: { name: string; note: string }[] = [
  { name: 'Rene masser', note: 'Stein og jord uten betong' },
  { name: 'Masser med betong og fliser', note: 'Betong og fliser, gjerne blandet med stein' },
  { name: 'Betongtakstein og skifer', note: 'Den vanligste typen takstein. Skifer har samme pris' },
  { name: 'Glassert eller malt takstein', note: 'Takstein med glassert eller malt overflate' },
];

export { RENTAL_DAYS } from './workdays';

export function rentalPeriodLabel(deliveryDate: string, extraDays = 0, pickupDate?: string | null): string {
  return `${formatNorwegianDate(deliveryDate)} – ${formatNorwegianDate(pickupDate || pickupDateFor(deliveryDate, extraDays).date)}`;
}

export const WEEKEND_PICKUP_NOTE = 'Faller hentedagen på en helg eller helligdag, henter vi neste virkedag uten ekstra kostnad. Henting i helgen kan avtales på telefon, mot et tillegg.';

export const MAX_EXTRA_RENTAL_DAYS = 25;
// Must match DRIVER_NOTE_MAX in the create-order function.
export const DRIVER_NOTE_MAX = 500;
export const DELIVERY_TIME_NOTE = 'Vi leverer i løpet av dagen, men kan ikke love et bestemt klokkeslett. Skjer det noe uventet, gir vi deg beskjed.';
// Must match WITHDRAWAL_WAIVER_VERSION in the create-order function.
export const WITHDRAWAL_WAIVER_VERSION = 'oppstart-for-angrefrist-v1-2026-10-27';
export const WITHDRAWAL_WAIVER_TEXT = 'Jeg ber om at oppdraget starter før angrefristen på 14 dager er ute. Jeg forstår at jeg må betale for det som er utført hvis jeg angrer etter at containeren er levert, og at angreretten går tapt når containeren er hentet.';
export const WITHDRAWAL_WAIVER_ERROR = 'Kryss av for å få levering innen angrefristen, eller velg en senere leveringsdato.';

export function isWithdrawalWaiverRequired(deliveryDate: string, now: Date = new Date()): boolean {
  if (!deliveryDate) return false;
  const todayOslo = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  const days = Math.round((Date.parse(`${deliveryDate}T00:00:00Z`) - Date.parse(`${todayOslo}T00:00:00Z`)) / 86400000);
  return days <= 21;
}

export const EXTRA_DAYS_NOTE = '5 dager er inkludert. Ekstra dager koster 25 kr per dag for 5 og 10 m³, og 35 kr per dag for 17 m³ og større. Trenger du containeren lenger enn du bestilte, ring oss, så forlenger vi til samme dagspris.';
export const UNAVAILABLE_TEXT = `Ikke tilgjengelig nå – ring oss på ${COMPANY.phone}`;

export const SACK_FRACTION = 'storsekker';
export const COURIER_FEE_NOK = 850;

export const SACK_SPECS: Record<string, { dimensions: string; liftCapacity: string }> = {
  '160 liter': { dimensions: '45 × 45 × 80 cm', liftCapacity: '250 kg' },
  '800 liter': { dimensions: '90 × 90 × 100 cm', liftCapacity: '1,5 tonn' },
  '2020 liter': { dimensions: '96 × 216 × 100 cm', liftCapacity: '1 tonn' },
};

export function containerLabel(opt: { size: string; type: string; subtype?: string | null }): string {
  if (opt.type === 'Sekk') return `Storsekk ${opt.size}`;
  return `${opt.size} ${opt.type}${opt.subtype ? ` – ${opt.subtype}` : ''}`;
}

function formatKr(value: number, decimals: number): string {
  return Number(value).toLocaleString('nb-NO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function formatOverweightRate(rate: number | null | undefined, fraction: FractionKey | string): string {
  if (rate == null) return 'Faktureres i tillegg';
  if (fraction === 'tunge_masser') return `${formatKr(Number(rate) * 1000, 0)} kr per tonn`;
  return `${formatKr(rate, 2)} kr per kg`;
}

export function overweightShortLabel(rate: number | null | undefined, fraction: FractionKey | string): string | null {
  if (rate == null) return null;
  if (fraction === 'tunge_masser') return `Overvekt ${formatKr(Number(rate) * 1000, 0)} kr/tonn`;
  return `Overvekt ${formatKr(rate, 2)} kr/kg`;
}

export function weightAmountFromKg(kg: number): string {
  if (kg >= 1000) return `${(kg / 1000).toLocaleString('nb-NO', { maximumFractionDigits: 1 })} tonn`;
  return `${kg} kg`;
}

export function weightLabelFromKg(kg: number | null | undefined): string {
  if (kg == null) return '–';
  return `Inntil ${weightAmountFromKg(kg)}`;
}

interface WeightOption {
  size: string;
  subtype?: string | null;
  includedWeightKg?: number | null;
}

const sizeNumber = (size: string) => size.replace(/\s*(kbm|m³)\s*$/, '');

function joinNorwegian(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} og ${items[items.length - 1]}`;
}

export function includedWeightSummary(opts: WeightOption[]): string {
  const bySize = new Map<string, number>();
  for (const o of opts) {
    if (o.includedWeightKg != null && !bySize.has(o.size)) bySize.set(o.size, o.includedWeightKg);
  }
  const sizesByWeight = new Map<number, string[]>();
  for (const [size, kg] of bySize) sizesByWeight.set(kg, [...(sizesByWeight.get(kg) || []), size]);
  const groups = [...sizesByWeight.entries()].sort((a, b) => a[0] - b[0]);
  if (groups.length === 0) return '–';
  if (groups.length === 1) return weightLabelFromKg(groups[0][0]);
  return groups
    .map(([kg, sizes]) => {
      const numbers = sizes.map(sizeNumber).sort((a, b) => parseFloat(a) - parseFloat(b));
      return `${weightAmountFromKg(kg)} (${joinNorwegian(numbers)} m³)`;
    })
    .join(' · ');
}

export function tungeMasserWeightNote(opts: WeightOption[]): string | null {
  const bySize = new Map<string, number>();
  for (const o of opts) {
    if (o.includedWeightKg != null) bySize.set(o.size, Math.max(bySize.get(o.size) ?? 0, o.includedWeightKg));
  }
  if (bySize.size === 0) return null;
  const sizesByWeight = new Map<number, string[]>();
  for (const [size, kg] of bySize) sizesByWeight.set(kg, [...(sizesByWeight.get(kg) || []), size]);
  return [...sizesByWeight.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([kg, sizes], i) => {
      const numbers = joinNorwegian(sizes.map(sizeNumber).sort((a, b) => parseFloat(a) - parseFloat(b)));
      return i === 0
        ? `Inntil ${weightAmountFromKg(kg)} er inkludert i ${numbers} m³`
        : `inntil ${weightAmountFromKg(kg)} i ${numbers} m³`;
    })
    .join(', og ');
}

export function campaignDiscountLabel(ordinaryPrice: number, campaignPrice: number): string {
  return `-${Math.round((1 - campaignPrice / ordinaryPrice) * 100)} %`;
}

